/**
 * 天地图 Provider —— 订单地图展示的正式实现（2026-08-28 任务7 R2）
 *
 * 边界铁律：T.* / tianditu.gov.cn / tk 只允许出现在本目录与 tianditu-loader；
 * 业务组件（OrderLocationMap 等）禁止 import 本文件，一切经 services/map。
 *
 * 官方 API（第一手，lbs.tianditu.gov.cn/api/js4.0/APIExample）：
 *   new T.Map(el)                                  容器（示例传 id 字符串）
 *   map.centerAndZoom(new T.LngLat(lng, lat), z)   经度在前（与高德同序）；必须初始化
 *   new T.Marker(new T.LngLat(lng, lat))
 *   map.addOverLay(marker)                         注意大小写：大写 L
 *   map.clearOverLays()
 *   map.checkResize()                              容器尺寸变化后重测（已从官方 API 源码确认存在）
 *
 * 坐标边界（§5 落定）：全库 GCJ-02 canonical → 天地图 CGCS2000(≈WGS84)。
 * 转换只发生在本 Provider 内（gcj02ToWgs84Exact，公开近似算法亚米级）；
 * 业务页面永不感知坐标系。白名单生效后须以真实地点实测校验 marker 落点。
 */
import { loadTianditu } from '../tianditu-loader.js'
import { gcj02ToWgs84Exact } from '../coordTransform.js'

// T.Map 构造参数需要容器 id（官方示例均为 id 字符串）；无 id 的容器现场生成
let idSeq = 0

// 隐藏版权控件（logo + 测绘资质署名）——应用层产品决策；一次性注入全局样式，幂等
let copyrightHidden = false
function hideCopyright () {
  if (copyrightHidden || typeof document === 'undefined') return
  copyrightHidden = true
  try {
    const s = document.createElement('style')
    s.textContent = '.tdt-control-copyright{display:none!important}'
    document.head.appendChild(s)
  } catch (e) { /* 无 head 环境忽略 */ }
}

// 构造门闩：等容器有真实布局尺寸（display:none 时 clientWidth=0）。
// SDK getSize() 首读即缓存（源码实证：Hw 缓存 + kW 脏标记），0 尺寸构造 = 永久裂图，
// 事后 checkResize 也救不回瓦片网格——必须在构造前保证尺寸就绪。
function waitForSize (el) {
  return new Promise((resolve, reject) => {
    const startedAt = Date.now()
    const tick = () => {
      if (el.clientWidth > 0 && el.clientHeight > 0) return resolve()
      if (Date.now() - startedAt > 600) return reject(new Error('container_no_size'))
      setTimeout(tick, 50)
    }
    tick()
  })
}

export default {
  name: 'tianditu',
  // 接受的输入坐标系：GCJ02（库内规范，转 CGCS2000 后渲染）/ WGS84（直用）
  supportedCoordinateSystems: ['GCJ02', 'WGS84'],

  /**
   * 创建地图视图（中心点 + Marker，订单详情场景）
   * @param {HTMLElement} el 容器（调用方保证已挂载且有尺寸）
   * @param {Object} p { key, latitude, longitude, coordinateSystem, zoom, interactive, minZoom, maxZoom }
   *   interactive 默认 true（2026-09-19 订单地图恢复交互：拖动/滚轮/捏合/双击全开，
   *   +/- 控件补装）；false = preview-only 关全部手势（旧锁定模式，保留 API）
   *   minZoom/maxZoom 合理缩放范围（不锁死）；默认 4/18
   * @returns {Promise<{destroy:Function, getZoom:Function, getCenter:Function}>}
   *   失败 reject——由上层降级，绝不抛进业务流程
   */
  async createMap (el, { key, latitude, longitude, coordinateSystem = 'GCJ02', zoom = 15, interactive = true, minZoom = 4, maxZoom = 18 }) {
    if (!this.supportedCoordinateSystems.includes(coordinateSystem)) {
      throw new Error('unsupported_coordinate_system:' + coordinateSystem)
    }
    const lat = Number(latitude)
    const lng = Number(longitude)
    if (isNaN(lat) || isNaN(lng)) throw new Error('bad_coordinate')

    // 坐标边界：GCJ02(库内规范) → WGS84(天地图原生)；境外恒等
    const wgs = coordinateSystem === 'GCJ02' ? gcj02ToWgs84Exact(lat, lng) : { lat, lng }

    const T = await loadTianditu({ key })
    await waitForSize(el)
    if (!el.id) el.id = 'tdt-map-' + (++idSeq)
    const map = new T.Map(el.id)
    const point = () => new T.LngLat(wgs.lng, wgs.lat)
    map.centerAndZoom(point(), zoom)
    const marker = new T.Marker(point())
    map.addOverLay(marker)
    hideCopyright()

    // 缩放范围（2026-09-19 恢复交互）：下限省域全景、上限街道级——自由缩放但不出合理域。
    // setMinZoom/setMaxZoom 存在则用 API；不存在/异常再由 zoomend 钳制兜底（版本差异守卫）。
    if (typeof map.setMinZoom === 'function') { try { map.setMinZoom(minZoom) } catch (e) { /* 兜底接管 */ } }
    if (typeof map.setMaxZoom === 'function') { try { map.setMaxZoom(maxZoom) } catch (e) { /* 兜底接管 */ } }
    let clamping = false
    if (typeof map.addEventListener === 'function') {
      try {
        map.addEventListener('zoomend', () => {
          if (clamping || typeof map.getZoom !== 'function') return
          const z = map.getZoom()
          if (z < minZoom || z > maxZoom) {
            clamping = true
            try { map.setZoom(Math.min(maxZoom, Math.max(minZoom, z))) } catch (e) { /* 忽略 */ }
            setTimeout(() => { clamping = false }, 60)
          }
        })
      } catch (e) { /* 事件挂载失败不影响渲染 */ }
    }

    // 交互模式：+/- 缩放控件补装（T.Control.Zoom；SDK 未内置，typeof 守卫防版本差异）。
    // preview-only（interactive:false）：关全部手势（拖拽/双击缩放/滚轮/捏合/键盘/惯性）——
    // 方法名逐一对照 SDK v4.1.6 源码（disableDrag/disableDragging 为别名对，重复调用无害）。
    if (interactive !== false) {
      try {
        if (T.Control && typeof T.Control.Zoom === 'function') map.addControl(new T.Control.Zoom())
      } catch (e) { /* 控件失败不影响地图本体 */ }
    } else {
      ['disableDrag', 'disableDragging', 'disableDoubleClickZoom', 'disableContinuousZoom',
        'disableScrollWheelZoom', 'disablePinchToZoom', 'disableKeyboard', 'disableInertia'
      ].forEach(fn => {
        try { if (typeof map[fn] === 'function') map[fn]() } catch (e) { /* 忽略单项 */ }
      })
    }

    let dead = false
    // 容器尺寸自愈：挂载后容器若再变化（dialog/折叠动画、迟到的布局），
    // 400ms 后 checkResize 重测+复位中心；尺寸没变时为无操作（内部比对 oldSize/newSize）。
    // ★ interactive 模式复位中心仅首 400ms 内（入场布局未稳窗口）；此后用户拖动不被拉回
    setTimeout(() => {
      if (dead) return
      try { map.checkResize(); map.centerAndZoom(point(), map.getZoom ? map.getZoom() : zoom) } catch (e) { /* 容器已卸载 */ }
    }, 400)

    return {
      destroy () {
        if (dead) return
        dead = true
        try { map.clearOverLays() } catch (e) { /* 已卸载则忽略 */ }
        // T.Map 未第一手确认 destroy() API：清容器 DOM 即彻底卸载瓦片/覆盖物/事件
        try { el.innerHTML = '' } catch (e) { /* 忽略 */ }
      },
      // 只读探针（消费方/E2E 断言用）：当前缩放级别与中心（GCJ-02 语义由调用方坐标决定）
      getZoom () {
        try { return typeof map.getZoom === 'function' ? map.getZoom() : null } catch (e) { return null }
      },
      getCenter () {
        try {
          if (typeof map.getCenter !== 'function') return null
          const c = map.getCenter()
          if (!c) return null
          if (typeof c.getLat === 'function') return { lat: c.getLat(), lng: c.getLng() }
          if (typeof c.lat === 'function') return { lat: c.lat(), lng: c.lng() }
          return { lat: c.lat, lng: c.lng }
        } catch (e) { return null }
      }
    }
  }
}
